import React, { useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel } from "@/components/ui/alert-dialog";
import { Upload, Eye, Trash2, Send, FileText, Image as ImageIcon } from "lucide-react";
import { abrirVisor } from "@/components/VisorArchivo";
import { fechaHoraMX } from "@/lib/format";

const ESTADOS_OK = ["Entregado", "Facturación", "Vencimiento", "Pago", "Pedido cerrado"];
const ACCEPT = ".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif,application/pdf,image/*";
const MAX = 5;

export const verRemision = (pedido, r) => abrirVisor(`/pedidos/${pedido.id}/remision/${r.id}`, r.nombre);

export function RemisionBotones({ pedido }) {
  const rems = pedido?.remisiones || [];
  return rems.map((r, i) => (
    <Button key={r.id} variant="outline" size="sm" className="text-emerald-700 border-emerald-700 hover:bg-emerald-50" onClick={() => verRemision(pedido, r)} data-testid={`remision-descargar-btn-${i}`}>
      <FileText className="h-4 w-4 mr-2" /> Remisión{rems.length > 1 ? ` ${i + 1}` : ""}
    </Button>
  ));
}

export default function RemisionArchivos({ pedido, estatusGuardado, onChange }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [sending, setSending] = useState(false);
  const rems = pedido.remisiones || [];
  const habilitado = ESTADOS_OK.includes(estatusGuardado ?? pedido.estatus);

  const subir = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    if (rems.length + files.length > MAX) { toast.error(`Máximo ${MAX} archivos de remisión por pedido`); return; }
    setBusy(true);
    let ultimo = null;
    for (const file of files) {
      try {
        const fd = new FormData(); fd.append("file", file);
        ({ data: ultimo } = await api.post(`/pedidos/${pedido.id}/remision`, fd));
      } catch (err) { toast.error(`${file.name}: ${err?.response?.data?.detail || err.message}`); }
    }
    setBusy(false);
    if (ultimo) { onChange(ultimo); toast.success("Remisión cargada"); }
  };
  const quitar = async (r) => {
    if (!window.confirm(`¿Quitar "${r.nombre}"?`)) return;
    try { const { data } = await api.delete(`/pedidos/${pedido.id}/remision/${r.id}`); onChange(data); }
    catch (err) { toast.error(`Error: ${err?.response?.data?.detail || err.message}`); }
  };
  const enviar = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/pedidos/${pedido.id}/remision-enviar`);
      onChange(data.pedido);
      if (data.email === "enviado") toast.success(`Remisión enviada a ${data.vendedor}`, { description: `Aviso en el CRM y correo a ${data.email_destino}` });
      else if (data.email === "sin_correo") toast.warning(`Aviso enviado a ${data.vendedor}`, { description: "El vendedor no tiene correo registrado; no se envió email." });
      else toast.warning(`Aviso enviado a ${data.vendedor}`, { description: "El correo no se pudo enviar en este momento." });
    } catch (err) { toast.error(`Error: ${err?.response?.data?.detail || err.message}`); }
    finally { setSending(false); setConfirm(false); }
  };

  return (
    <div className="space-y-2 md:col-span-2 border rounded-lg p-3 bg-slate-50" data-testid="remision-archivos">
      <Label>Remisión firmada (comprobante de entrega)</Label>
      {!habilitado ? (
        <p className="text-xs text-muted-foreground" data-testid="remision-no-disponible">Podrás subir la remisión cuando el pedido esté en "Entregado" o después (guarda primero el cambio de estatus).</p>
      ) : (
        <>
          {rems.length === 0 && <p className="text-sm text-muted-foreground" data-testid="remision-vacia">Sin remisión</p>}
          {rems.map((r, i) => {
            const Icon = (r.content_type || "").startsWith("image/") ? ImageIcon : FileText;
            return (
              <div key={r.id} className="flex items-center gap-2 rounded-md border bg-white px-3 py-2" data-testid={`remision-fila-${i}`}>
                <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
                <button type="button" className="text-sm flex-1 truncate text-left hover:underline" onClick={() => verRemision(pedido, r)} data-testid={`remision-nombre-${i}`}>{r.nombre}</button>
                <Button type="button" variant="ghost" size="sm" title="Ver" onClick={() => verRemision(pedido, r)} data-testid={`remision-descargar-${i}`}><Eye className="h-4 w-4 mr-1" />Ver</Button>
                <Button type="button" variant="ghost" size="icon" title="Quitar" className="text-red-600" onClick={() => quitar(r)} data-testid={`remision-quitar-${i}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            );
          })}
          <input ref={ref} type="file" multiple accept={ACCEPT} className="hidden" onChange={subir} data-testid="remision-input" />
          <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
            <Button type="button" variant="outline" size="sm" disabled={busy || rems.length >= MAX} onClick={() => ref.current?.click()} data-testid="remision-subir">
              <Upload className="h-4 w-4 mr-1" />{busy ? "Subiendo..." : rems.length ? "Agregar hoja" : "Subir remisión"}
            </Button>
            <Button type="button" size="sm" disabled={!rems.length || sending} onClick={() => setConfirm(true)} data-testid="remision-enviar"><Send className="h-4 w-4 mr-1" />{sending ? "Enviando..." : "Enviar remisión al vendedor"}</Button>
          </div>
          <p className="text-xs text-muted-foreground" data-testid="remision-enviada-info">{pedido.remision_enviada_at ? `Enviada el ${fechaHoraMX(pedido.remision_enviada_at)}` : "Aún no se ha enviado al vendedor"} · PDF o foto, hasta {MAX} archivos de 10 MB</p>
        </>
      )}
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent data-testid="remision-enviar-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Enviar remisión del pedido {pedido.numero_pedido} a {pedido.vendedor?.full_name || "el vendedor"}?</AlertDialogTitle>
            <AlertDialogDescription>
              {pedido.remision_enviada_at ? `Ya se envió el ${fechaHoraMX(pedido.remision_enviada_at)}; se mandará de nuevo. ` : ""}
              Le llegará un aviso en el módulo Avisos con {rems.length > 1 ? `los ${rems.length} archivos` : "el archivo"} y un correo electrónico.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="remision-enviar-cancelar">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); enviar(); }} disabled={sending} data-testid="remision-enviar-confirmar">{sending ? "Enviando..." : "Sí, enviar"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
